/**
 * ลิ้นชักเงินสด
 *
 * เงินที่ควรมี = เงินต้นรอบ
 *              + เงินสดสุทธิจากการขาย   (คืนเงินติดลบอยู่แล้ว)
 *              + เงินเข้าออกลิ้นชัก      (เงินออกติดลบอยู่แล้ว)
 *
 * ไม่มีเงื่อนไขพิเศษสำหรับการคืนเงินหรือเงินออก เพราะเครื่องหมายถูกบังคับ
 * ตั้งแต่ตอนบันทึกด้วย CHECK constraint ในฐานข้อมูล
 */

import { assertSatang, sumSatang } from './money.ts';
import type { CashMovement, PaymentRow } from './types.ts';

/** ธนบัตรและเหรียญไทยที่หมุนเวียนอยู่ (หน่วยสตางค์) */
export const THAI_DENOMINATIONS = [
  100, 200, 500, 1_000, 2_000, 5_000, 10_000, 50_000, 100_000,
] as const;

/** นับเงินจากจำนวนธนบัตร/เหรียญแต่ละชนิด */
export function countDenominations(
  counts: ReadonlyMap<number, number>,
): number {
  let total = 0;
  for (const [denomination, qty] of counts) {
    assertSatang(denomination, 'denomination');
    if (!Number.isInteger(qty) || qty < 0) {
      throw new Error(`จำนวนของ ${denomination} ต้องเป็นจำนวนเต็มไม่ติดลบ`);
    }
    total += denomination * qty;
  }
  return total;
}

/** เงินสดสุทธิจากการขาย — นับเฉพาะช่องทางที่เป็นเงินสดจริง */
export function cashFromSales(rows: readonly PaymentRow[]): number {
  return sumSatang(
    rows.filter((r) => r.methodKind === 'cash').map((r) => r.amountSatang),
    'payment.amountSatang',
  );
}

/** เงินเข้าออกลิ้นชักสุทธิ — รวมทุกประเภท ไม่ใช่แค่ค่าใช้จ่าย */
export function netCashMovements(movements: readonly CashMovement[]): number {
  return sumSatang(
    movements.map((m) => m.amountSatang),
    'movement.amountSatang',
  );
}

export interface DrawerReconciliation {
  readonly expectedSatang: number;
  readonly countedSatang: number;
  /** บวก = เงินเกิน · ลบ = เงินขาด */
  readonly varianceSatang: number;
}

export function reconcileDrawer(input: {
  openingFloatSatang: number;
  payments: readonly PaymentRow[];
  movements: readonly CashMovement[];
  countedSatang: number;
}): DrawerReconciliation {
  assertSatang(input.openingFloatSatang, 'openingFloatSatang');
  assertSatang(input.countedSatang, 'countedSatang');

  const expectedSatang =
    input.openingFloatSatang +
    cashFromSales(input.payments) +
    netCashMovements(input.movements);

  return {
    expectedSatang,
    countedSatang: input.countedSatang,
    varianceSatang: input.countedSatang - expectedSatang,
  };
}

/**
 * เหตุผลที่ลิ้นชักถูกสั่งเปิด — บันทึกไว้เพื่อสอบทานย้อนหลังได้
 *
 * ลิ้นชักที่เปิดโดยไม่มีเหตุผลรองรับ คือสัญญาณแรกที่ควรสังเกตเวลาเงินขาด
 */
export type DrawerOpenReason =
  | 'cash_payment'          // รับเงินสดจากลูกค้า
  | 'cash_refund'           // คืนเงินสดให้ลูกค้า
  | 'drawer_session_open'   // เปิดรอบตอนเช้า ใส่เงินทอน
  | 'drawer_session_close'  // ปิดรอบ นับเงิน
  | 'cash_movement'         // เบิกเงินออก หรือเติมเงินทอนระหว่างวัน
  | 'reprint_receipt';      // พิมพ์ใบเสร็จซ้ำที่มีการรับเงินสด

/**
 * ★ ลิ้นชักเปิดเฉพาะเมื่อมีเงินสดเกี่ยวข้องเท่านั้น ★
 *
 * ระบบเดิมเปิดลิ้นชัก **ทุกครั้ง** ที่ปิดบิล รวมถึงตอนลูกค้าโอนเงินและสั่ง
 * ผ่านเดลิเวอรี่ ซึ่งไม่มีเงินสดเข้าลิ้นชักเลย
 *
 * จากข้อมูลจริง โอนเงินคือ 62% และเดลิเวอรี่ 17% ของรายได้ — แปลว่าลิ้นชัก
 * เดิมเปิดโดยไม่จำเป็นเกือบ 4 ใน 5 ครั้ง ทั้งเปลืองกลไกและเป็นช่องเสี่ยง
 *
 * การคืนเงินสดก็ต้องเปิด เพราะต้องหยิบเงินออกมาให้ลูกค้า
 */
export function shouldOpenDrawer(rows: readonly PaymentRow[]): boolean {
  return rows.some((r) => r.methodKind === 'cash' && r.amountSatang !== 0);
}

/** เหตุผลที่จะบันทึก เมื่อการชำระเงินนั้นทำให้ลิ้นชักต้องเปิด */
export function drawerReasonFor(
  rows: readonly PaymentRow[],
): DrawerOpenReason | null {
  const cash = rows.filter((r) => r.methodKind === 'cash' && r.amountSatang !== 0);
  if (cash.length === 0) return null;
  // มีการรับเงินสดปนอยู่ด้วยถือเป็นการรับเงิน แม้จะมีการคืนในบิลเดียวกัน
  return cash.some((r) => r.amountSatang > 0) ? 'cash_payment' : 'cash_refund';
}

/**
 * เงินต้นของรอบถัดไป
 *
 * ตอนปิดรอบพนักงานเลือกว่าจะเก็บเงินไว้ในลิ้นชักเท่าไร ที่เหลือนำส่ง
 * ยอดที่เก็บไว้กลายเป็นเงินต้นของรอบพรุ่งนี้
 */
export function nextOpeningFloat(keepSatang: number): number {
  assertSatang(keepSatang, 'keepSatang');
  if (keepSatang < 0) {
    throw new Error('เงินที่เก็บไว้ติดลบไม่ได้');
  }
  return keepSatang;
}
